/**
 * arlo-event-list.js
 *
 * Shared script for displaying Arlo EventSearch API results on any page.
 *
 * USAGE
 * -----
 * Add data attributes to the section element that contains the event list:
 *   data-arlo-template-code  — Arlo TemplateCode string(s), comma-separated (e.g. "ES" or "OG10,OC10")
 *   data-arlo-template-id    — Arlo EventTemplateID number(s), comma-separated (e.g. "7" or "3,30")
 *
 * Inside that section, place two child elements with data-arlo-role:
 *   data-arlo-role="status"  — receives loading / result count / error text
 *   data-arlo-role="list"    — <ul> that receives the rendered <li> items
 *
 * TIMEZONE HANDLING
 * -----------------
 * Online events (Location.IsOnline === true) are always displayed in Eastern Time
 * (EDT in summer, EST in winter) regardless of how they are stored in Arlo.
 *
 * In-person events display times exactly as stored in Arlo, using the TimeZone
 * abbreviation the API returns (e.g. CDT, PDT).
 */

(function () {
  // Arlo public EventSearch API endpoint
  // For instance https://tpc1.arlo.co/api/2012-02-01/pub/resources/eventsearch/?top=200&fields=Name,EventTemplateID,TemplateCode,StartDateTime,EndDateTime,TimeZone,Location,AdvertisedOffers,RegistrationInfo
  // var apiUrl = 'https://tpctraining.arlo.co/api/2012-02-01/pub/resources/eventsearch/';
  var apiUrl = 'https://tpc1.arlo.co/api/2012-02-01/pub/resources/eventsearch/';

  // ---------------------------------------------------------------------------
  // DATE / TIME HELPERS — in-person events
  //
  // For in-person events we parse directly from the ISO 8601 string characters
  // instead of using new Date(), which would apply browser or UTC offset math
  // and potentially shift the displayed time. This preserves the exact local
  // time Arlo stored for the event's venue timezone.
  // ---------------------------------------------------------------------------

  // Extracts year/month/day/hour/minute from an ISO 8601 string like
  // "2026-04-01T08:00:00.0000000-05:00" without any timezone conversion.
  function parseLocalFromIso(isoString) {
    var match = String(isoString || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return null;
    return {
      year: +match[1],
      month: +match[2],
      day: +match[3],
      hour: +match[4],
      minute: +match[5],
    };
  }

  // Formats a parsed local date object as a long date string, e.g. "April 1, 2026".
  // Uses a local Date constructed without offset math so no shifting occurs.
  function formatDateFromLocal(parts) {
    if (!parts) return 'Date unavailable';
    var d = new Date(parts.year, parts.month - 1, parts.day);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  // Formats a parsed local time object as a short time string, e.g. "8:00 AM".
  // Uses an arbitrary fixed date (Jan 1 2000) just to leverage toLocaleTimeString
  // formatting without any date-related offset effects.
  function formatTimeFromLocal(parts) {
    if (!parts) return 'Time unavailable';
    var d = new Date(2000, 0, 1, parts.hour, parts.minute);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // ---------------------------------------------------------------------------
  // DATE / TIME HELPERS — online events (Eastern Time)
  // For online events we want to display the correct Eastern Time regardless of what UTC offset the event was stored with in Arlo
  // ---------------------------------------------------------------------------

  // Formats an ISO datetime string as a long date in Eastern Time.
  function formatDateEastern(isoString) {
    var d = new Date(isoString);
    if (isNaN(d.getTime())) return 'Date unavailable';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York',
    });
  }

  // Formats an ISO datetime string as a short time in Eastern Time.
  function formatTimeEastern(isoString) {
    var d = new Date(isoString);
    if (isNaN(d.getTime())) return 'Time unavailable';
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
    });
  }

  // Returns the correct Eastern Time abbreviation for a given datetime —
  // "EDT" during Daylight Saving Time and "EST" outside of it.
  // Uses Intl.DateTimeFormat so DST transitions are handled automatically.
  function getEasternAbbreviation(isoString) {
    var d = new Date(isoString);
    if (isNaN(d.getTime())) return 'ET';
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        timeZoneName: 'short',
      }).formatToParts(d);
      var tzPart = parts.find(function (p) {
        return p.type === 'timeZoneName';
      });
      return tzPart ? tzPart.value : 'ET';
    } catch (e) {
      return 'ET';
    }
  }

  // ---------------------------------------------------------------------------
  // TIMEZONE TEXT — in-person events
  // ---------------------------------------------------------------------------

  // Converts a raw UTC offset string (e.g. "-0500" or "+0530") to a readable label like "UTC-05:00". Used only when the API does not return an abbreviation for in-person events.
  function normalizeUtcOffset(offsetValue) {
    if (!offsetValue) {
      return '';
    }

    // "Z" suffix means UTC
    if (offsetValue === 'Z') {
      return 'UTC';
    }

    var normalized = String(offsetValue).trim();

    if (!normalized) {
      return '';
    }

    // Insert colon if missing, converting e.g. "-0500" → "-05:00"
    if (/^[+-]\d{2}\d{2}$/.test(normalized)) {
      normalized = normalized.slice(0, 3) + ':' + normalized.slice(3);
    }

    if (/^[+-]\d{2}:\d{2}$/.test(normalized)) {
      return 'UTC' + normalized;
    }

    return '';
  }

  // Returns the timezone label for an in-person event. Checks the API's
  // TimeZone field first (e.g. "CDT"), then falls back to parsing the UTC
  // offset out of the StartDateTime ISO string.
  function getTimezoneText(eventItem) {
    var timezoneCandidates = [
      eventItem && eventItem.TimeZoneAbbreviation,
      eventItem && eventItem.TimeZone,
      eventItem && eventItem.Timezone,
    ];

    // Use the first non-empty candidate string
    var timezoneValue = timezoneCandidates.find(function (value) {
      return typeof value === 'string' && value.trim();
    });

    if (timezoneValue) {
      return String(timezoneValue).trim();
    }

    // Fallback: extract the offset from the end of the ISO datetime string
    var dateValue = eventItem && eventItem.StartDateTime ? String(eventItem.StartDateTime) : '';
    var offsetMatch = dateValue.match(/(Z|[+-]\d{2}:?\d{2})$/);

    if (offsetMatch && offsetMatch[1]) {
      return normalizeUtcOffset(offsetMatch[1]);
    }

    return '';
  }

  // ---------------------------------------------------------------------------
  // LOCATION
  // ---------------------------------------------------------------------------

  // Returns a human-readable location string from the event's Location object.
  // Handles both plain string values and the object shape the API returns
  // (e.g. { Name: "Online", IsOnline: true }).
  function getLocationText(eventItem) {
    var directLocation = eventItem && eventItem.Location;

    if (typeof directLocation === 'string' && directLocation.trim()) {
      return directLocation.trim();
    }

    if (directLocation && typeof directLocation === 'object') {
      if (directLocation.Name) {
        return String(directLocation.Name);
      }

      if (directLocation.Summary) {
        return String(directLocation.Summary);
      }
    }

    return 'Location unavailable';
  }

  // Returns a display-ready price from AdvertisedOffers.
  // Prefers tax-exclusive formatted price and falls back to tax-inclusive.
  function getPriceText(eventItem) {
    var offers = eventItem && Array.isArray(eventItem.AdvertisedOffers) ? eventItem.AdvertisedOffers : [];
    if (!offers.length) {
      return '';
    }

    var selectedOffer = offers.find(function (offer) {
      return offer && offer.OfferAmount;
    });

    if (!selectedOffer || !selectedOffer.OfferAmount) {
      return '';
    }

    var amount = selectedOffer.OfferAmount;
    var formattedPrice = amount.FormattedAmountTaxExclusive || amount.FormattedAmountTaxInclusive || '';
    return String(formattedPrice).replace(/\.00\b/, '');
  }

  // ---------------------------------------------------------------------------
  // UTILITIES
  // ---------------------------------------------------------------------------

  // Escapes special HTML characters to prevent XSS when inserting API data
  // into innerHTML.
  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Checks whether an event matches the template filters configured on the
  // page via data-arlo-template-code and data-arlo-template-id.
  // Returns true if either a code or an ID matches, or if no filters are set.
  function isMatchingTemplate(eventItem, targetTemplateCodes, targetEventTemplateIds) {
    var templateCode = eventItem && eventItem.TemplateCode != null ? String(eventItem.TemplateCode) : '';
    var templateId = eventItem && eventItem.EventTemplateID != null ? Number(eventItem.EventTemplateID) : null;
    var hasCodeFilters = targetTemplateCodes.length > 0;
    var hasIdFilters = targetEventTemplateIds.length > 0;

    // No filters means show everything the API returns
    if (!hasCodeFilters && !hasIdFilters) {
      return true;
    }

    if (hasCodeFilters && targetTemplateCodes.includes(templateCode)) {
      return true;
    }

    if (hasIdFilters && templateId !== null && !isNaN(templateId) && targetEventTemplateIds.includes(templateId)) {
      return true;
    }

    return false;
  }

  // Splits a comma-separated string into a trimmed, non-empty array.
  // Used to support multiple template codes or IDs in a single data attribute.
  function parseCsvValues(inputValue) {
    if (inputValue == null) {
      return [];
    }

    return String(inputValue)
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);
  }

  // ---------------------------------------------------------------------------
  // ARLO API REQUEST
  //
  // The Arlo API is on a different domain, so we use JSONP (script tag injection)
  // to work around the browser's cross-origin restrictions without needing a
  // server-side proxy. A unique callback name is generated for each request
  // to support multiple simultaneous calls on the same page.
  // ---------------------------------------------------------------------------
  function jsonpRequest(url, params) {
    return new Promise(function (resolve, reject) {
      // Unique global callback name so multiple calls don't collide
      var callbackName = 'arloJsonp_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
      var script = document.createElement('script');
      var timeout;
      var query = [];

      // Tell the API which callback function name to wrap the response in
      params.callback = callbackName;

      // Build query string from all params
      Object.keys(params).forEach(function (key) {
        query.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
      });

      // The API calls this function with the JSON payload as its argument
      window[callbackName] = function (payload) {
        clearTimeout(timeout);
        delete window[callbackName]; // Clean up global
        script.remove();
        resolve(payload);
      };

      // Handle network errors (e.g. script fails to load)
      script.onerror = function () {
        clearTimeout(timeout);
        delete window[callbackName];
        script.remove();
        reject(new Error('JSONP request failed'));
      };

      // Safety timeout in case the API hangs or the callback is never called
      timeout = setTimeout(function () {
        delete window[callbackName];
        script.remove();
        reject(new Error('JSONP request timed out'));
      }, 15000);

      script.src = url + '?' + query.join('&');
      document.body.appendChild(script);
    });
  }

  // ---------------------------------------------------------------------------
  // RENDERING
  // ---------------------------------------------------------------------------

  var eventsPerPage = 8;

  function buildEventCardHtml(eventItem) {
    // Determine whether this is an online event by checking the Location field
    var isOnline = eventItem.Location && (eventItem.Location.IsOnline === true || eventItem.Location.Name === 'Online');

    var dateText, timeText, endTimeText, endDateText, timezoneText;

    if (isOnline) {
      // Online events: convert all times to Eastern Time (EDT/EST) using
      // Intl.DateTimeFormat so DST is handled correctly based on the event date.
      dateText = formatDateEastern(eventItem.StartDateTime);
      timeText = formatTimeEastern(eventItem.StartDateTime);
      endTimeText = eventItem.EndDateTime ? formatTimeEastern(eventItem.EndDateTime) : '';
      endDateText = '';
      if (eventItem.EndDateTime) {
        var startDayE = formatDateEastern(eventItem.StartDateTime);
        var endDayE = formatDateEastern(eventItem.EndDateTime);
        // Only show an end date if the event spans multiple calendar days
        if (startDayE !== endDayE) endDateText = endDayE;
      }
      timezoneText = getEasternAbbreviation(eventItem.StartDateTime);
    } else {
      // In-person events: parse time characters directly from the ISO string
      // to preserve exactly the local time Arlo stored, without any conversion.
      var startLocal = parseLocalFromIso(eventItem.StartDateTime);
      var endLocal = eventItem.EndDateTime ? parseLocalFromIso(eventItem.EndDateTime) : null;
      dateText = formatDateFromLocal(startLocal);
      timeText = formatTimeFromLocal(startLocal);
      endTimeText = endLocal ? formatTimeFromLocal(endLocal) : '';
      endDateText = '';
      if (endLocal) {
        // Compare dates as integers (YYYYMMDD) to detect multi-day events
        var startDayKey = startLocal.year * 10000 + startLocal.month * 100 + startLocal.day;
        var endDayKey = endLocal.year * 10000 + endLocal.month * 100 + endLocal.day;
        if (startDayKey !== endDayKey) endDateText = formatDateFromLocal(endLocal);
      }
      timezoneText = getTimezoneText(eventItem);
    }

    var locationText = getLocationText(eventItem);
    var priceText = getPriceText(eventItem);

    // Use the direct registration URL if available, otherwise fall back to
    // the event detail page URL (ViewUri)
    var registerUri =
      eventItem.RegistrationInfo && eventItem.RegistrationInfo.RegisterUri
        ? eventItem.RegistrationInfo.RegisterUri
        : eventItem.ViewUri;

    // Build the list item HTML. All API values are escaped before insertion.
    var dateLine = escapeHtml(dateText) + (endDateText ? ' &ndash; ' + escapeHtml(endDateText) : '');
    var timeLine =
      escapeHtml(timeText) +
      (endTimeText ? ' &ndash; ' + escapeHtml(endTimeText) : '') +
      (timezoneText ? ' (' + escapeHtml(timezoneText) + ')' : '');
    var escapedLocationText = escapeHtml(locationText);
    var escapedPriceText = escapeHtml(priceText);
    var escapedRegisterUri = escapeHtml(registerUri);

    var registerHtml = registerUri
      ? `
        <a class="cta-btn blue small w-auto px-3 arlo-api-event-register" href="${escapedRegisterUri}" target="_blank">
          Register
        </a>
      `
      : '<span class="cta-btn blue outline small w-auto px-3 arlo-api-event-register disabled" aria-disabled="true">Unavailable</span>';

    // return `
    //   <div class="col-10 col-sm-6 col-md-4 col-xl-3 d-flex">
    //     <div class="card p-3 bg-light mb-3 w-100">
    //       <div class="card-body p-0 arlo-api-event-info">
    //         <p class="fw-semibold mb-1">${dateLine}</p>
    //         <p class="arlo-api-event mb-1 border-bottom">${timeLine}</p>
    //         ${locationText ? `<p class="arlo-api-event mb-1">${escapedLocationText}</p>` : ''}
    //         ${priceText ? `<p class="arlo-api-event fw-semibold mb-1">${escapedPriceText}</p>` : ''}
    //       </div>
    //       <div class="card-footer border-0 bg-transparent text-center mb-0">
    //         ${registerHtml}
    //       </div>
    //     </div>
    //   </div>
    // `;
    return `
      <div class="col-10 col-sm-6 col-md-4 col-xl-3 d-flex">
        <div class="card p-3 bg-light mb-3 w-100">
          <div class="card-body p-0 arlo-api-event-info">
            <p class="fw-semibold mb-0">${dateLine}</p>
            <p class="arlo-api-event small mb-2 border-bottom">${timeLine}</p>
            ${locationText ? `<p class="arlo-api-event mb-1">${escapedLocationText}</p>` : ''}
          </div>
          <div class="card-footer border-0 bg-transparent text-center mb-0">
            ${registerHtml}
          </div>
        </div>
      </div>
    `;
  }

  function getLoadMoreButton(listElement) {
    var buttonWrap = listElement.parentNode.querySelector("[data-arlo-role='load-more-wrap']");

    if (!buttonWrap) {
      buttonWrap = document.createElement('div');
      buttonWrap.className = 'text-center mt-2';
      buttonWrap.setAttribute('data-arlo-role', 'load-more-wrap');

      var button = document.createElement('a');
      button.href = '#';
      button.className = 'cta-btn blue outline small w-auto px-3';
      button.textContent = 'Load More Dates';
      button.setAttribute('data-arlo-role', 'load-more');

      buttonWrap.appendChild(button);
      listElement.parentNode.appendChild(buttonWrap);
    }

    return buttonWrap.querySelector("[data-arlo-role='load-more']");
  }

  // Builds and injects the event list HTML into the page.
  function renderEvents(events, statusElement, listElement) {
    if (!statusElement || !listElement) {
      return;
    }

    if (!events.length) {
      statusElement.className = 'text-center';
      statusElement.textContent = 'No upcoming sessions found.';
      listElement.setAttribute('hidden', 'hidden');
      listElement.innerHTML = '';

      var emptyStateButton = listElement.parentNode.querySelector("[data-arlo-role='load-more-wrap']");
      if (emptyStateButton) {
        emptyStateButton.setAttribute('hidden', 'hidden');
      }

      return;
    }

    var visibleCount = Math.min(eventsPerPage, events.length);
    var loadMoreButton = getLoadMoreButton(listElement);

    function renderVisibleEvents() {
      var html = '';

      events.slice(0, visibleCount).forEach(function (eventItem) {
        html += buildEventCardHtml(eventItem);
      });

      listElement.innerHTML = html;
      listElement.removeAttribute('hidden');

      if (visibleCount >= events.length) {
        loadMoreButton.parentNode.setAttribute('hidden', 'hidden');
      } else {
        loadMoreButton.parentNode.removeAttribute('hidden');
      }
    }

    loadMoreButton.onclick = function (event) {
      event.preventDefault();
      visibleCount = Math.min(visibleCount + eventsPerPage, events.length);
      renderVisibleEvents();
    };

    statusElement.className = '';
    statusElement.textContent = '';
    renderVisibleEvents();
  }

  // ---------------------------------------------------------------------------
  // DATA LOADING
  // ---------------------------------------------------------------------------

  // Reads the template filters from a container element's data attributes, fetches matching events from the Arlo API, and renders them.
  function loadContainer(container) {
    // Read comma-separated template codes and IDs from the element's data attributes
    var targetTemplateCode = container.getAttribute('data-arlo-template-code') || '';
    var templateIdAttribute = container.getAttribute('data-arlo-template-id');
    var targetTemplateCodes = parseCsvValues(targetTemplateCode);
    var targetEventTemplateIds = parseCsvValues(templateIdAttribute)
      .map(function (value) {
        return Number(value);
      })
      .filter(function (value) {
        return !isNaN(value);
      });

    // Find the status and list elements inside this container
    var statusElement = container.querySelector("[data-arlo-role='status']");
    var listElement = container.querySelector("[data-arlo-role='list']");

    // Set initial loading message with spinner
    if (statusElement) {
      statusElement.className = 'arlo-api-loading';
      statusElement.innerHTML = '<div class="arlo-api-spinner"></div><span>Loading available sessions...</span>';
    }

    // Fetch up to 200 upcoming events with only the fields we need.
    // The 'fields' parameter controls what the API includes in each item.
    jsonpRequest(apiUrl, {
      format: 'json',
      top: 200,
      fields:
        'Name,EventTemplateID,TemplateCode,StartDateTime,EndDateTime,TimeZone,Location,AdvertisedOffers,RegistrationInfo,ViewUri',
    })
      .then(function (response) {
        var allItems = response && response.Items ? response.Items : [];

        // Keep only events matching this page's configured template code(s)/ID(s)
        var filteredItems = allItems.filter(function (eventItem) {
          return isMatchingTemplate(eventItem, targetTemplateCodes, targetEventTemplateIds);
        });

        // Sort chronologically by start date
        filteredItems.sort(function (a, b) {
          return new Date(a.StartDateTime) - new Date(b.StartDateTime);
        });

        renderEvents(filteredItems, statusElement, listElement);
      })
      .catch(function () {
        if (statusElement) {
          statusElement.className = '';
          statusElement.textContent = 'Unable to load sessions right now. Please try again soon.';
        }
      });
  }

  // ---------------------------------------------------------------------------
  // INITIALIZATION
  // ---------------------------------------------------------------------------

  // Finds all containers on the page that have Arlo template attributes and loads data for each one. This allows multiple independent event lists on the same page if needed.
  function init() {
    var containers = document.querySelectorAll('[data-arlo-template-code], [data-arlo-template-id]');

    if (!containers.length) {
      return;
    }

    containers.forEach(function (container) {
      loadContainer(container);
    });
  }

  // Run init after the DOM is ready. If the DOM is already parsed (e.g. the script was deferred), call init immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
